import React from 'react';
import { ThemeProvider, createMuiTheme } from '@material-ui/core';
import palette from './palette';
import typography from './typography';
import breakpoint from './breakpoints';

export const accelerateThemeSettings = {
  breakpoints: {
    values: {
      xs: breakpoint.xs,
      sm: breakpoint.sm,
      md: breakpoint.md,
      lg: breakpoint.lg,
      xl: breakpoint.xl,
    },
  },
  palette: {
    divider: palette.gmGray,
    error: {
      main: palette.gmDarkRed,
      contrastText: palette.gmWhite,
    },
    info: {
      main: palette.gmPrimaryBlue,
      contrastText: palette.gmWhite,
    },
    primary: {
      main: palette.gmPrimaryBlue,
      light: palette.gmLightBlue,
      dark: palette.gmMediumBlue,
      contrastText: palette.gmWhite,
    },
    secondary: {
      main: palette.gmBlack,
      light: palette.gmLightGray,
      dark: palette.gmDarkGray,
      contrastText: palette.gmWhite,
    },
    success: {
      main: palette.gmGreen,
      contrastText: palette.gmWhite,
    },
    text: {
      primary: palette.gmBlack,
      secondary: palette.gmDarkGray,
    },
    warning: {
      main: palette.gmYellow,
      contrastText: palette.gmBlack,
    },
  },
  overrides: {
    MuiAccordion: {
      root: {
        border: `solid 1px ${palette.gmGray}`,
        boxShadow: 'none',
        '&:not(:last-child)': {
          borderBottom: 0,
        },
        '&:before': {
          display: 'none',
        },
        '&$expanded': {
          margin: 'auto',
        },
        '&.gds-nested-accordion': {
          border: 'none'
        }
      },
    },
    MuiAccordionDetails: {
      root: {
        backgroundColor: palette.gmLightestGray,
        padding: 16,
        '&.gds-nested-accordion-details': {
          padding: 0,
        }
      },
    },
    MuiAccordionSummary: {
      root: {
        borderBottom: `solid 1px ${palette.gmGray}`,
        marginBottom: -1,
        '&$expanded': {
          minHeight: 48,
        },
        '&.gds-nested-accordion-summary': {
          borderBottom: 'none',
          padding: 0,
          background: palette.gmLightestGray
        }
      },
      content: {
        '&$expanded': {
          margin: '12px 0',
        },
      },
    },
    MuiAlert: {
      filledWarning: {
        color: palette.gmBlack,
      },
      message: {
        fontSize: 14,
      },
      outlinedError: {
        backgroundColor: palette.gmPink,
        color: palette.gmBlack,
      },
      outlinedInfo: {
        backgroundColor: palette.gmLightestBlue,
        color: palette.gmBlack,
      },
      outlinedSuccess: {
        backgroundColor: palette.gmLightGreen,
        color: palette.gmBlack,
      },
      outlinedWarning: {
        backgroundColor: palette.gmLightYellow,
        color: palette.gmBlack,
      },
      root: {
        lineHeight: '20px',
      },
    },
    MuiAlertTitle: {
      root: {
        fontSize: 16,
        marginTop: -1,
      },
    },
    MuiBadge: {
      root: {
        '&.gds-badge-gray': {
          '& $badge': {
            color: palette.gmWhite,
            backgroundColor: palette.gmDarkGray,
          },
        },
        '&.gds-badge-green': {
          '& $badge': {
            color: palette.gmWhite,
            backgroundColor: palette.gmGreen,
          },
        },
        '&.gds-badge-red': {
          '& $badge': {
            color: palette.gmWhite,
            backgroundColor: palette.gmRed,
          },
        },
      },
    },
    MuiBreadcrumbs: {
      root: {
        '&.gds-breadcrumb': {
          fontSize: 12,
        },
      },
      li: {
        '&.gds-breadcrumb-children': {
          fontWeight: 'normal',
          'text-transform': 'capitalize',
        },
      },
    },
    MuiButton: {
      label: {
        'text-transform': 'capitalize',
      },
      root: {
        '&.gds-button-destructive': {
          paddingLeft: 16,
          paddingRight: 16,
          backgroundColor: palette.gmRed,
          color: palette.gmWhite,
          '&:hover': {
            backgroundColor: palette.gmRedSecond,
          },
          '&:active:hover': {
            backgroundColor: palette.gmDarkRed,
          },
          '&:disabled': {
            backgroundColor: palette.gmPink,
            color: palette.gmWhite,
            pointerEvents: 'auto',
            '&:hover': {
              backgroundColor: palette.gmPink,
              color: palette.gmWhite,
              pointerEvents: 'auto',
              cursor: 'not-allowed',
            }
          },
        },
        '&.gds-button-primary': {
          paddingLeft: 16,
          paddingRight: 16,
          backgroundColor: palette.gmPrimaryBlue,
          color: palette.gmWhite,
          '&:hover': {
            backgroundColor: palette.gmMediumBlue,
          },
          '&:active:hover': {
            backgroundColor: palette.gmDarkBlue,
          },
          '&:disabled': {
            backgroundColor: palette.gmDisabledBlue,
            color: palette.gmWhite,
            pointerEvents: 'auto',
            '&:hover': {
              backgroundColor: palette.gmDisabledBlue,
              color: palette.gmWhite,
              pointerEvents: 'auto',
              cursor: 'not-allowed',
            }
          },
        },
        '&.gds-button-primary-navigation': {
          color: palette.gmWhite,
          height: 48,
          '&:hover': {
            backgroundColor: palette.gmDarkBlue,
            color: palette.gmWhite,
          },
          '&.active': {
            backgroundColor: palette.gmDarkBlue,
            color: palette.gmWhite,
          },
        },
        '&.gds-button-secondary': {
          paddingLeft: 16,
          paddingRight: 16,
          backgroundColor: palette.gmWhite,
          color: palette.gmPrimaryBlue,
          boxShadow: `inset 0 0 0 1px ${palette.gmPrimaryBlue}`,
          '&:hover': {
            boxShadow: `inset 0 0 0 1px ${palette.gmMediumBlue}`,
            color: palette.gmMediumBlue,
          },
          '&:active:hover': {
            boxShadow: `inset 0 0 0 1px ${palette.gmDarkBlue}`,
            color: palette.gmDarkBlue,
          },
          '&:disabled': {
            boxShadow: `inset 0 0 0 1px ${palette.gmDisabledBlue}`,
            color: palette.gmDisabledBlue,
            pointerEvents: 'auto',
            '&:hover': {
              boxShadow: `inset 0 0 0 1px ${palette.gmDisabledBlue}`,
              color: palette.gmDisabledBlue,
              pointerEvents: 'auto',
              cursor: 'not-allowed',
            }
          },
        },
        '&.gds-button-split-dropdown': {
          minWidth: 20,
          width: 20,
        },
        '&.gds-button-tertiary': {
          paddingLeft: 16,
          paddingRight: 16,
          backgroundColor: 'transparent',
          color: palette.gmPrimaryBlue,
          '&:hover': {
            color: palette.gmMediumBlue,
          },
          '&:active:hover': {
            color: palette.gmDarkBlue,
          },
          '&:disabled': {
            color: palette.gmDisabledBlue,
            pointerEvents: 'auto',
            '&:hover': {
              color: palette.gmDisabledBlue,
              pointerEvents: 'auto',
              cursor: 'not-allowed',
            }
          },
        },
        '&.gds-button-workflow': {
          paddingLeft: 16,
          paddingRight: 16,
          backgroundColor: palette.gmWhite,
          color: palette.gmPrimaryBlue,
          boxShadow: `inset 0 0 0 1px ${palette.gmLightGray}`,
          '&:hover': {
            color: palette.gmMediumBlue,
            backgroundColor: palette.gmLighterGray,
          },
          '&:active:hover': {
            color: palette.gmDarkBlue,
            backgroundColor: palette.gmLighterGray,
          },
          '&:disabled': {
            backgroundColor: palette.gmWhite,
            color: palette.gmDisabledBlue,
            pointerEvents: 'auto',
            '&:hover': {
              pointerEvents: 'auto',
              cursor: 'not-allowed',
              backgroundColor: palette.gmWhite,
              color: palette.gmDisabledBlue,
            }
          },
        },
      },
      outlined: {
        '&.gds-button-group-inactive': {
          borderColor: palette.gmGray,
          marginRight: '0 !important',
          marginLeft: 0,
        },
        '&.gds-button-group-active': {
          borderColor: palette.gmPrimaryBlue,
          backgroundColor: palette.gmPrimaryBlue,
          color: palette.gmWhite,
          marginRight: '0 !important',
        },
      },
    },
    MuiCheckbox: {
      root: {
        color: palette.gmGray,
        '&:hover': {
          color: palette.gmDarkGray,
          backgroundColor: 'transparent',
        },
        '&$checked': {
          color: palette.gmPrimaryBlue,
          '&:hover': {
            color: palette.gmPrimaryBlue,
            backgroundColor: 'transparent',
          },
        },
        '&$disabled': {
          color: palette.gmLightGray,
          '&:hover': {
            color: palette.gmLightGray,
            backgroundColor: 'transparent',
          },
        },
        '&.gds-checkbox-read-only': {
          color: palette.gmGray,
          '&:hover': {
            color: palette.gmGray,
            backgroundColor: 'transparent',
          },
        },
        '&.gds-checkbox-error': {
          color: palette.gmRed,
          '&:hover': {
            color: palette.gmRed,
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiChip: {
      root: {
        borderRadius: 3,
        minHeight: 24,
        '&.gds-chip-actionable': {
          backgroundColor: palette.gmLightestBlue,
          border: `solid 1px ${palette.gmLightBlue}`,
          color: palette.gmBlack,
          '&:focus': {
            backgroundColor: palette.gmLightestBlue,
            border: `solid 1px ${palette.gmLightBlue}`,
            color: palette.gmBlack,
          }
        },
        '&.gds-chip-non-actionable': {
          backgroundColor: palette.gmGray,
          color: palette.gmBlack,
        },
        '&.gds-chip-non-actionable-error': {
          backgroundColor: palette.gmDarkRed,
          color: palette.gmWhite,
        },
        '&.gds-chip-non-actionable-info': {
          backgroundColor: palette.gmPrimaryBlue,
          color: palette.gmWhite,
        },
        '&.gds-chip-non-actionable-success': {
          backgroundColor: palette.gmDarkGreen,
          color: palette.gmWhite,
        },
        '&.gds-chip-non-actionable-warn': {
          backgroundColor: palette.gmYellow,
          color: palette.gmBlack,
        },
        '&.gds-chip-non-actionable-light': {
          backgroundColor: palette.gmLightestGray,
          border: `solid 1px ${palette.gmGray}`,
          color: palette.gmBlack,
        },
        '&.gds-chip-non-actionable-light-error': {
          backgroundColor: palette.gmPink,
          border: `solid 1px ${palette.gmDarkRed}`,
          color: palette.gmBlack,
        },
        '&.gds-chip-non-actionable-light-info': {
          backgroundColor: palette.gmLightestBlue,
          border: `solid 1px ${palette.gmPrimaryBlue}`,
          color: palette.gmBlack,
        },
        '&.gds-chip-non-actionable-light-success': {
          backgroundColor: palette.gmLightGreen,
          border: `solid 1px ${palette.gmGreen}`,
          color: palette.gmBlack,
        },
        '&.gds-chip-non-actionable-light-warn': {
          backgroundColor: palette.gmLightYellow,
          border: `solid 1px ${palette.gmYellow}`,
          color: palette.gmBlack,
        },
      },
    },
    MuiDialogActions: {
      root: {
        padding: '1rem',
      },
    },
    MuiDialogContent: {
      root: {
        padding: '1rem',
      },
    },
    MuiDialogTitle: {
      root: {
        backgroundColor: palette.gmLighterGray,
        borderBottom: `solid 1px ${palette.gmGray}`,
        display: 'flex',
        alignItems: 'center',
        padding: '1rem',
        '&.dialog-title': {
          fontSize: '1.2rem',
          fontWeight: 'bold',
        },
      },
    },
    MuiDrawer: {
      root: {
        '&.gds-mega-menu': {
          zIndex: '1298 !important',
          '& .MuiDrawer-paperAnchorTop': {
            top: 104,
            padding: '1rem',
          },
        },
      },
    },
    MuiFormControlLabel: {
      root: {
        '&.gds-formLabel-read-only': {
          '& span.MuiTypography-root': {
            color: palette.gmBlack,
          },
        },
        '&.gds-formLabel-error': {
          '& span.MuiTypography-root': {
            color: palette.gmRedSecond,
          },
        },
      },
    },
    MuiInput: {
      underline: {
        '&:before': {
          transition: 'none',
        },
        '&:hover:not(.Mui-disabled):before': {
          borderBottomWidth: 1,
          borderBottomStyle: 'solid',
        },
      },
    },
    MuiLinearProgress: {
      root: {
        '&.gds-linear-progress': {
          backgroundColor: palette.gmLighterGray,
        },
      },
      bar: {
        '&.gds-linear-progress-error': {
          backgroundColor: palette.gmRed,
        },
      },
    },
    MuiLink: {
      underlineHover: {
        color: palette.gmPrimaryBlue,
        '&:hover': {
          color: palette.gmMediumBlue,
        },
        '&:visited': {
          color: palette.gmPurple,
        },
      },
    },
    MuiListItem: {
      root: {
        '&.Mui-selected': {
          backgroundColor: palette.gmLightestBlue,
          '&:hover': {
            backgroundColor: palette.gmLightestBlue,
          },
        },
      },
    },
    MuiRadio: {
      root: {
        color: palette.gmGray,
        '&:hover': {
          color: palette.gmDarkGray,
          backgroundColor: 'transparent',
        },
        '&$checked': {
          color: palette.gmPrimaryBlue,
          '&:hover': {
            color: palette.gmPrimaryBlue,
            backgroundColor: 'transparent',
          },
        },
        '&$disabled': {
          color: palette.gmLightGray,
          '&:hover': {
            color: palette.gmLightGray,
            backgroundColor: 'transparent',
          },
        },
        '&.gds-radio-read-only': {
          color: palette.gmGray,
          '&:hover': {
            color: palette.gmGray,
            backgroundColor: 'transparent',
          },
        },
        '&.gds-radio-error': {
          color: palette.gmRed,
          '&:hover': {
            color: palette.gmRed,
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiStepButton: {
      root: {
        padding: '.5rem 1rem',
        '&:hover': {
          backgroundColor: palette.gmLighterGray,
        },
      },
    },
    MuiStepLabel: {
      active: {
        'font-weight': 'bold !important'
      }
    },
    MuiSwitch: {
      root: {
        '&.gds-switch': {
          width: 42,
          height: 26,
          padding: 0,
          margin: '1rem',
          '& .MuiSwitch-track': {
            borderRadius: 26 / 2,
            backgroundColor: palette.gmGray,
            opacity: 1,
          },
          '& .MuiSwitch-switchBase': {
            padding: 1,
            color: palette.gmWhite,
            top: 1,
            left: 1,
            '& span .MuiSwitch-thumb': {
              width: 22,
              height: 22,
              boxShadow: 'none',
            },
            '&$checked': {
              transform: 'translateX(16px)',
              color: palette.gmWhite,
              '& + $track': {
                backgroundColor: palette.gmPrimaryBlue,
                opacity: 1,
              },
            },
            '&$disabled': {
              color: palette.gmWhite,
              '& + $track': {
                backgroundColor: palette.gmLightGray,
                opacity: 1,
              },
              '&$checked': {
                transform: 'translateX(16px)',
                color: palette.gmWhite,
                '& + $track': {
                  backgroundColor: palette.gmDisabledBlue,
                  opacity: 1,
                },
              },
            },
          },
        },
      },
    },
    MuiTable: {
      root: {
        '&.gds-table': {
          border: `solid 1px ${palette.gmGray}`,
        },
      },
    },
    MuiTableCell: {
      root: {
        '&:not(:last-child)': {
          borderRight: `solid 1px ${palette.gmGray}`,
        },
        '&.gds-table-cell': {
          borderBottom: 'none',
        },
      },
      head: {
        'font-weight': 'bold',
        color: palette.gmDarkGray,
      }
    },
    MuiTableHead: {
      root: {
        '&.gds-table-header': {
          backgroundColor: palette.gmLightestGray,
          borderBottom: `solid 1px ${palette.gmGray}`,
        },
      },
    },
    MuiTableRow: {
      root: {
        '&:not(:last-child)': {
          borderBottom: `solid 1px ${palette.gmGray}`,
        },
        '&.gds-table-row-striped': {
          '&:not(:last-child)': {
            borderBottom: `solid 1px ${palette.gmGray}`,
          },
          '&:nth-of-type(even)': {
            backgroundColor: palette.gmLightestGray,
          },
          '&:nth-of-type(odd)': {
            backgroundColor: palette.gmWhite,
          },
        },
      },
    },
    MuiTab: {
      root: {
        '&.gds-primary-tab': {
          opacity: 1,
          border: `solid 1px ${palette.gmGray}`,
          borderRight: 'none',
          '&:last-child': {
            borderRight: `solid 1px ${palette.gmGray}`,
          },
          color: palette.gmDarkGray,
          fontSize: '1rem',
          'text-transform': 'capitalize',
          '&$selected': {
            fontWeight: 'bold',
            backgroundColor: palette.gmLightGray,
            color: palette.gmBlack,
            '&:hover': {
              backgroundColor: palette.gmLightGray,
            },
          },
          '&:hover': {
            color: palette.gmBlack,
            backgroundColor: palette.gmLighterGray,
            opacity: 1,
          },
        },
        '&.gds-nested-tab': {
          opacity: 1,
          color: palette.gmDarkGray,
          minHeight: 28,
          padding: 0,
          minWidth: 50,
          marginRight: '1rem',
          'text-transform': 'capitalize',
          '&:last-child': {
            marginRight: 0
          },
          '&$selected': {
            color: palette.gmBlack,
            fontWeight: 'bold',
            boxShadow: `inset 0 -3px 0 0 ${palette.gmPrimaryBlue}`,
          },
          '&:hover': {
            color: palette.gmBlack,
            opacity: 1,
          },
        },
        '&.gds-scoped-tab': {
          opacity: 1,
          border: `solid 1px ${palette.gmGray}`,
          borderBottom: 'none',
          borderRight: 'none',
          '&:last-child': {
            borderRight: `solid 1px ${palette.gmGray}`,
          },
          color: palette.gmDarkGray,
          fontSize: '1rem',
          'text-transform': 'capitalize',
          '&$selected': {
            fontWeight: 'bold',
            backgroundColor: palette.gmLightGray,
            color: palette.gmBlack,
            '&:hover': {
              backgroundColor: palette.gmLightGray,
            },
          },
          '&:hover': {
            color: palette.gmBlack,
            backgroundColor: palette.gmLighterGray,
            opacity: 1,
          },
        },
        '&.Mui-disabled': {
          opacity: '1 !important',
          color: `${palette.gmGray} !important`,
          pointerEvents: 'auto',
          '&:hover': {
            opacity: '1 !important',
            color: `${palette.gmGray} !important`,
            backgroundColor: 'transparent !important',
            pointerEvents: 'auto',
            cursor: 'not-allowed',
          }
        }
      },
    },
    MuiTabs: {
      indicator: {
        height: 0,
      },
    },
    MuiToggleButton: {
      root: {
        borderColor: palette.gmGray,
        color: palette.gmBlack,
        '&.Mui-selected': {
          borderColor: palette.gmPrimaryBlue,
          backgroundColor: palette.gmPrimaryBlue,
          color: palette.gmWhite,
          '&:hover': {
            backgroundColor: `${palette.gmPrimaryBlue} !important`,
            color: palette.gmWhite,
          },
        },
      },
    },
    MuiToolbar: {
      root: {
        '&.gds-application-header': {
          minHeight: '3.5rem',
          padding: '0 0.5rem',
          backgroundColor: palette.gmWhite,
          '&& img.gds-header-logo': {
            height: 40,
            width: 40,
            marginRight: '.5rem',
          },
        },
      },
      dense: {
        '&.gds-primary-navigation': {
          backgroundColor: palette.gmPrimaryBlue,
        },
        '&.gds-footer': {
          padding: 0,
          paddingRight: '.5rem',
          paddingLeft: '.5rem',
          color: palette.gmWhite,
          backgroundColor: palette.gmBlack,
        },
      },
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: palette.gmBlack,
        fontSize: '0.85rem',
        fontWeight: 400,
        color: palette.gmWhite,
      },
      arrow: {
        color: palette.gmBlack,
      },
    },
    MuiTypography: {
      root: {
        '&.gds-breadcrumb-active': {
          fontSize: 12,
          fontWeight: 600,
          color: palette.gmDarkGray,
        },
        '&.gds-breadcrumb-inactive': {
          '&& a': {
            fontSize: 12,
            color: palette.gmDarkGray,
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
        },
      },
    },
  },
  props: {
    MuiButtonBase: {
      disableRipple: true,
    },
  },
  shape: {
    borderRadius: 2,
  },
  typography: {
    fontSize: 14,
    fontFamily: typography.fontFamily,
    h1: typography.h1,
    h2: typography.h2,
    h3: typography.h3,
    h4: typography.h4,
    h5: typography.h5,
    h6: typography.h6,
    body1: typography.body1,
    body2: typography.body2,
  },
};

export const AccelerateThemeProvider = ({ children }: any) => {
  const theme = createMuiTheme({
    ...accelerateThemeSettings,
  });
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};
